/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Box, MenuItem, TextField } from '@mui/material';
import { DATE_RANGE_DROPDOWN_VALUES, FUTURE_PAST_OPTIONS } from 'modules/scenario/constants';
import { getActualOption } from 'modules/scenario/function';
import { useState } from 'react';

const DateRangeFilter = ({ value, onChange }) => {
  const [selected, setSelected] = useState(
    typeof value === 'string' ? getActualOption(value) : 'Custom Date Range'
  );

  const handleCountChange = (val) => {
    const parts = selected?.split(' ');
    onChange(`${parts[0]} ${val} ${parts[1]}`);
  };

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    setSelected(val);

    if (FUTURE_PAST_OPTIONS.includes(val)) {
      handleCountChange(1);
    } else if (val !== 'Custom Date Range') {
      onChange(val);
    } else {
      onChange({ startDate: '', endDate: '' });
    }
  };

  const handleDateChange = (key, val) => {
    const newValue = {
      ...(typeof value === 'object' ? value : {}),
      [key]: val,
    };
    onChange(newValue);
  };

  return (
    <Box sx={{ display: 'flex', gap: 3, flexDirection: 'column' }}>
      <TextField
        select
        label="Date Range"
        value={selected}
        onChange={handleDropdownChange}
        size="small"
        sx={{ minWidth: 140 }}
      >
        {DATE_RANGE_DROPDOWN_VALUES.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
      {FUTURE_PAST_OPTIONS.includes(selected) && (
        <TextField
          label={`How Many ${selected.split?.(' ')?.[1]}`}
          type="number"
          value={value?.split?.(' ')?.[1] || 1}
          onChange={(e) => handleCountChange(Number(e.target.value))}
          size="small"
          InputProps={{ inputProps: { min: 1 } }}
          sx={{ maxWidth: 120 }}
        />
      )}
      {selected === 'Custom Date Range' && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            value={value?.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />

          <TextField
            label="End Date"
            type="date"
            value={value?.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default DateRangeFilter;
